import React from "react";
import { useMutation } from "react-query";
import {
  ALL_RINKS,
  findRink,
  firstEnrolledVendor,
  getRinks,
  Rink,
  setActiveSelection,
  VendorId,
  VENDOR_LABEL,
} from "./vendor";
import { authenticate } from "./bootAuth";
import { Button, Dropdown, Header, Input, Page, Section } from "./components";
import penguin from "./penguin_rocket.gif";
import { navigate } from ".";

const VENDOR_ORDER: VendorId[] = ["recus", "daysmart"];

function defaultRinkId(): string {
  const first = firstEnrolledVendor();
  if (first) return getRinks(first)[0].id;
  return ALL_RINKS[0].id;
}

export function Auth() {
  const [selectedRinkId, setSelectedRinkId] = React.useState<string>(
    defaultRinkId
  );
  const rink = findRink(selectedRinkId) ?? ALL_RINKS[0];
  const vendor = rink.vendor;

  const [username, setUsername] = React.useState("");
  const [password, setPassword] = React.useState("");

  const authMutation = useMutation({
    mutationFn: async () => {
      await authenticate(vendor, { credentials: { username, password } });
      setActiveSelection(vendor, selectedRinkId);
      navigate("/");
    },
  });

  return (
    <Page>
      <Header />
      <Section>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            authMutation.mutate();
          }}
          className="w-full flex flex-col items-center"
        >
          <div className="mb-4 w-full">
            <RinkPicker value={selectedRinkId} onPick={setSelectedRinkId} />
          </div>
          <div className="mb-4 w-full">
            <Input
              placeholder="username / email"
              value={username}
              setValue={setUsername}
            />
          </div>
          <div className="mb-4 w-full">
            <Input
              placeholder="password"
              value={password}
              setValue={setPassword}
              type="password"
            />
          </div>
          {authMutation.isLoading ? (
            <img
              className="w-20 h-20 opacity-80 rounded absolute -bottom-2"
              src={penguin}
              alt="loading"
            />
          ) : (
            <Button
              onClick={() => authMutation.mutate()}
              textClass="text-xl"
              fullWidth
            >
              login
            </Button>
          )}
          {authMutation.isError && (
            <div className="mt-3 text-sm text-red-600 w-full text-center">
              wrong username or password
            </div>
          )}
        </form>
      </Section>
      <div className="my-10"></div>
      <Section>
        <h2 className="w-full text-lg mb-3 select-text">how do i use this?</h2>
        <div className="w-full text-lg select-text">
          <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-3">
            <div className="text-lg">❄️</div>
            <div>
              login with your rink info, and view freestyle ice skating
              schedules!
            </div>
            <div className="text-lg">🎄</div>
            <div>
              we don't store any of your login or payment info — we just talk to
              the rink providers on your behalf.
            </div>
            <div className="text-lg">☃️</div>
            <div>
              skeetbowl is non-official + unaffiliated with any organization or
              rink! support may be limited.
            </div>
          </div>
        </div>
      </Section>
    </Page>
  );
}

function RinkPicker(props: { value: string; onPick: (id: string) => void }) {
  const [open, setOpen] = React.useState(false);
  const current = findRink(props.value);

  const byVendor: Record<VendorId, Rink[]> = {
    daysmart: getRinks("daysmart"),
    recus: getRinks("recus"),
  };

  return (
    <Dropdown
      trigger={
        <button
          type="button"
          className="h-12 px-3 w-full text-lg rounded border-4 border-border font-light bg-bg-brand-alt text-text-on-brand-alt cursor-pointer flex items-center justify-between"
        >
          <span className="truncate">{current?.name ?? ""}</span>
          <span className="text-sm opacity-70">▾</span>
        </button>
      }
      open={open}
      onOpenChange={setOpen}
      align="left"
      className="w-full p-2 bg-bg-brand-alt text-text-on-brand-alt text-xl"
    >
      {VENDOR_ORDER.map((v, i) => (
        <div key={v}>
          {i > 0 && (
            <div className="border-t border-border-on-brand-alt my-2" />
          )}
          <div className="px-2 py-1 text-base text-text-on-brand-alt/60">
            {VENDOR_LABEL[v]}
          </div>
          {byVendor[v].map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => {
                props.onPick(r.id);
                setOpen(false);
              }}
              className={
                "w-full text-left p-2 rounded cursor-pointer hover:bg-black/10 " +
                (r.id === props.value ? "font-medium" : "")
              }
            >
              {r.name}
            </button>
          ))}
        </div>
      ))}
    </Dropdown>
  );
}
